import { useActive, useAttrs, useChainedCommands } from '@remirror/react'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowSquareOut,
  Check,
  IconProps,
  Link,
  ListBullets,
  ListNumbers,
  TextBolder,
  TextItalic,
  TextStrikethrough,
  TextUnderline,
  X,
} from '@phosphor-icons/react'
import React, {
  ComponentPropsWithoutRef,
  FC,
  forwardRef,
  useState,
} from 'react'

type ToolbarButtonProps = ComponentPropsWithoutRef<'button'> & {
  active?: boolean
  icon: FC<IconProps>
  label: string
}

// A single toolbar control. Styled to the check-in-cycle wizard palette
// (muted slate, navy when active) rather than the floating menu's greys.
const ToolbarButton: FC<ToolbarButtonProps> = ({
  icon: Icon,
  active,
  onClick,
  label,
  ...props
}) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    className={cn(
      'flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-[#E4E8F2]',
      active ? 'text-[#1D264A]' : 'text-[#5E6580]',
    )}
    // Preventing mousedown default keeps the click from moving focus out of the
    // editor and collapsing the current text selection before the command runs.
    // Scoped to the buttons (not the whole toolbar) so the link URL input can
    // still receive and keep focus normally.
    onMouseDown={(e) => e.preventDefault()}
    onClick={(e) => {
      e.preventDefault()
      onClick?.(e)
    }}
    {...props}
  >
    <Icon size={18} weight="bold" />
  </button>
)

const Separator = () => <div className="mx-1.5 h-5 w-px bg-[#D5DAE8]" />

// Inline URL editor, shown overlaying the toolbar row when Link is toggled.
const LinkForm: FC<{
  onSubmit: (url: string | undefined) => void
  onRemove: () => void
}> = ({ onRemove, onSubmit }) => {
  const { link } = useAttrs()
  const existing = link()?.href as string | undefined
  const [url, setUrl] = useState(existing ?? '')

  return (
    <motion.div
      className="absolute inset-0 flex items-center rounded-md bg-white px-2"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
    >
      <input
        type="text"
        autoFocus
        className="w-full border-0 p-1 text-sm text-[#1D264A] focus:outline-none focus:ring-0"
        placeholder="Enter URL..."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onSubmit(url)
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            onSubmit(existing)
          }
        }}
      />
      <button
        type="button"
        aria-label="Apply link"
        className="ml-2 px-2 text-[#5E6580] transition-colors hover:text-[#1D264A]"
        onClick={() => onSubmit(url)}
      >
        <Check size={16} />
      </button>
      {existing && (
        <a
          href={existing}
          target="_blank"
          rel="noreferrer"
          className="text-[#5E6580] transition-colors hover:text-[#1D264A]"
        >
          <ArrowSquareOut size={16} />
        </a>
      )}
      <button
        type="button"
        aria-label="Remove link"
        className="px-2 text-[#5E6580] transition-colors hover:text-[#1D264A]"
        onClick={onRemove}
      >
        <X size={16} />
      </button>
    </motion.div>
  )
}

// A persistent toolbar pinned to the top of the editor (vs the default
// selection-following FloatingToolbar). Exposes only the marks the cycle/email
// fields allow: bold, italic, underline, strikethrough, ordered + bullet lists,
// and links.
export const StaticMenu = forwardRef<HTMLDivElement>((_props, ref) => {
  const chain = useChainedCommands()
  const active = useActive()
  const [linkActive, setLinkActive] = useState(false)

  return (
    <div ref={ref} className="relative mb-3 flex items-center">
      <ToolbarButton
        onClick={() => chain.toggleBold().focus().run()}
        active={active.bold()}
        icon={TextBolder}
        label="Bold"
      />
      <ToolbarButton
        onClick={() => chain.toggleItalic().focus().run()}
        active={active.italic()}
        icon={TextItalic}
        label="Italic"
      />
      <ToolbarButton
        onClick={() => chain.toggleUnderline().focus().run()}
        active={active.underline()}
        icon={TextUnderline}
        label="Underline"
      />
      <ToolbarButton
        onClick={() => chain.toggleStrike().focus().run()}
        active={active.strike()}
        icon={TextStrikethrough}
        label="Strikethrough"
      />
      <Separator />
      <ToolbarButton
        onClick={() => chain.toggleOrderedList().focus().run()}
        active={active.orderedList()}
        icon={ListNumbers}
        label="Ordered list"
      />
      <ToolbarButton
        onClick={() => chain.toggleBulletList().focus().run()}
        active={active.bulletList()}
        icon={ListBullets}
        label="Bullet list"
      />
      <Separator />
      <ToolbarButton
        onClick={() => setLinkActive(true)}
        active={active.link()}
        icon={Link}
        label="Link"
      />
      <AnimatePresence>
        {linkActive && (
          <LinkForm
            onRemove={() => {
              setLinkActive(false)
              chain.removeLink().focus().run()
            }}
            onSubmit={(url) => {
              setLinkActive(false)
              if (url) chain.updateLink({ href: url }).focus().run()
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
})

StaticMenu.displayName = 'StaticMenu'
